import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@nexus/ui";
import { Archive, Loader2, RotateCcw } from "lucide-react";
import { usePermissions, useIamPermission } from "@/features/auth/hooks/usePermissions";
import {
  useAccessTemplates,
  useChangeAccessTemplateStatus,
} from "@/features/identity-hub/hooks/useIdentityHub";
import type { AccessTemplateDto } from "@/features/identity-hub/api/identity-hub.api";
import type { OrgPickerItem } from "./OrgPickerCombobox";
import { TemplateDialog } from "./TemplateDialog";
import { CatalogRoleDrawer } from "./CatalogRoleDrawer";
import { TemplatesTable } from "./TemplatesTable";
import { TemplatesToolbar, type TemplateFilters } from "./TemplatesToolbar";
import { IamListError, IamListSkeleton } from "./IamListError";
import { PREVENT_DISMISS_PROPS } from "./shared/dialog/prevent-dismiss-on-outside";

interface TemplatesPaneProps {
  orgs: OrgPickerItem[];
  activeTenantId: string | null;
}

export function TemplatesPane({ orgs, activeTenantId }: TemplatesPaneProps) {
  const { t } = useTranslation();
  const perms = usePermissions();
  const canList = useIamPermission(perms.IDENTITY_HUB_LIST_TEMPLATES);
  const canCreate = useIamPermission(perms.IDENTITY_HUB_CREATE_TEMPLATE);
  const canEdit = useIamPermission(perms.IDENTITY_HUB_UPDATE_TEMPLATE);
  const canChangeStatus = useIamPermission(perms.IDENTITY_HUB_ARCHIVE_TEMPLATE);

  const [filters, setFilters] = useState<TemplateFilters>({ search: "", showArchived: false });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AccessTemplateDto | null>(null);
  const [statusTarget, setStatusTarget] = useState<AccessTemplateDto | null>(null);

  const templatesQuery = useAccessTemplates({
    search: filters.search || undefined,
    includeArchived: filters.showArchived,
  }, { enabled: canList });
  const archivedQuery = useAccessTemplates(
    { includeArchived: true },
    { enabled: canList && !filters.showArchived },
  );
  const changeStatus = useChangeAccessTemplateStatus();

  const rows = templatesQuery.data ?? [];
  const archivedCount = useMemo(
    () => (archivedQuery.data ?? []).filter((r) => r.status === "ARCHIVED").length,
    [archivedQuery.data],
  );

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (row: AccessTemplateDto) => {
    if (!canEdit) return;
    setDialogOpen(false);
    setEditing(row);
  };

  // Archive/restore acts in the owning organization, matching the create/edit path.
  const amberTenantId = statusTarget
    ? (statusTarget.canBeEditedByTenantId ?? statusTarget.ownerTenant?.id ?? statusTarget.tenantIds[0] ?? activeTenantId)
    : activeTenantId;

  return (
    <>
      <TemplatesToolbar
        filters={filters}
        onFilterChange={(key, value) => setFilters((prev) => ({ ...prev, [key]: value }))}
        onNewTemplate={openCreate}
        canCreate={canCreate}
        archivedCount={filters.showArchived ? 0 : archivedCount}
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        {templatesQuery.isError ? (
          <IamListError
            error={templatesQuery.error}
            onRetry={() => { void templatesQuery.refetch(); }}
          />
        ) : templatesQuery.isLoading && rows.length === 0 ? (
          <IamListSkeleton />
        ) : templatesQuery.isFetching && rows.length === 0 ? (
          <div className="flex justify-center py-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : (
          <TemplatesTable
            rows={rows}
            onEdit={openEdit}
            onChangeStatus={canChangeStatus ? setStatusTarget : undefined}
            canEdit={canEdit}
            canChangeStatus={canChangeStatus}
          />
        )}
      </div>

      <TemplateDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
        }}
        orgs={orgs}
        activeTenantId={activeTenantId}
        editing={null}
      />

      <CatalogRoleDrawer
        open={!!editing}
        onOpenChange={(open) => { if (!open) setEditing(null); }}
        orgs={orgs}
        activeTenantId={activeTenantId}
        editing={editing}
      />

      <Dialog open={!!statusTarget} onOpenChange={(v) => { if (!v) setStatusTarget(null); }}>
        <DialogContent className="max-w-sm" {...PREVENT_DISMISS_PROPS}>
          <DialogHeader>
            <div className="mb-1 flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
              {statusTarget?.status === "ARCHIVED" ? (
                <RotateCcw className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Archive className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
            <DialogTitle>
              {statusTarget?.status === "ARCHIVED"
                ? t("iam.templates.statusDialog.restoreTitle", "Restore role?")
                : t("iam.templates.statusDialog.archiveTitle", "Archive role?")}
            </DialogTitle>
            <DialogDescription>
              {statusTarget && (
                <>
                  <strong className="text-foreground">{statusTarget.name}</strong>
                  {statusTarget.status === "ARCHIVED"
                    ? t("iam.templates.statusDialog.restoreBody", " will be restored to the catalog.")
                    : t("iam.templates.statusDialog.archiveBody", " will be archived. Existing access groups are unchanged.")}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <Button variant="outline" size="sm" onClick={() => setStatusTarget(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              size="sm"
              disabled={changeStatus.isPending || !amberTenantId}
              onClick={() => {
                if (!statusTarget || !amberTenantId) return;
                void changeStatus.mutateAsync({
                  templateId: statusTarget.id,
                  tenantId: amberTenantId,
                  status: statusTarget.status === "ARCHIVED" ? "ACTIVE" : "ARCHIVED",
                }).then(() => {
                  setStatusTarget(null);
                }).catch(() => {
                  // useAmberMutation already toasts the error; keep the dialog open.
                });
              }}
            >
              {changeStatus.isPending
                ? t("iam.templates.statusDialog.pending", "Updating…")
                : statusTarget?.status === "ARCHIVED"
                  ? t("iam.templates.statusDialog.restoreConfirm", "Restore role")
                  : t("iam.templates.statusDialog.archiveConfirm", "Archive role")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
